/**
 * useDraftEstimate — TanStack Query del endpoint /draft/estimate.
 *
 * Cache 60s por (artifactId, sectionId, profileName). Se invalida al
 * cambiar profile o al re-abrir el modal después de una corrida real
 * (los nuevos llm_usage_records cambian las estimaciones futuras
 * marginalmente, pero ese efecto es despreciable).
 *
 * Sin LLM calls — el backend solo lee el profile activo y devuelve
 * rangos de tokens. Tan barato que podríamos llamarlo en el render del
 * editor; la cache lo protege de loops.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import {
  type DraftEstimate,
  estimateSectionDraft,
} from '../api/agents'
import { useAccessToken } from '../auth/useAccessToken'

export function useDraftEstimate(
  args: {
    artifactId: string | null
    sectionId: string | null
    sectionType?: string | null
    profileName?: string | null
    versionId?: string | null
    enabled?: boolean
  },
): UseQueryResult<DraftEstimate, Error> {
  const { accessToken } = useAccessToken()
  const enabled =
    (args.enabled ?? true) &&
    !!accessToken &&
    !!args.artifactId &&
    !!args.sectionId

  return useQuery({
    queryKey: [
      'draft-estimate',
      args.artifactId,
      args.sectionId,
      args.profileName ?? null,
      args.sectionType ?? null,
      args.versionId ?? null,
    ],
    queryFn: () => {
      if (!accessToken || !args.artifactId || !args.sectionId) {
        throw new Error('not_ready')
      }
      return estimateSectionDraft(
        accessToken,
        args.artifactId,
        args.sectionId,
        {
          profileName: args.profileName ?? null,
          sectionType: args.sectionType ?? null,
          versionId: args.versionId ?? null,
        },
      )
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}
